<template>
  <div ref="chartEl" id="verticalBarChart" :style="{'min-height': height + 'px'}" class="echart"></div>
</template>

<script setup>
import {computed, onMounted, onBeforeUnmount, ref, watch} from 'vue';
import {getCurrencyFormatted, getCurrencySymbol} from "@/api/helpers";

const props = defineProps({
  title: String,
  planTitle: String,
  factTitle: String,
  currency: String,
  type: String,
  height: String,
  elements: {type: Array, default: () => []},
});

const chartEl = ref(null);
let chart = null;

const chartValues = computed(() =>
    (props.elements || []).map(el => ({
      plan: Number(el.plan ?? 0),
      fact: Number(el.fact ?? 0),
      name: el.name ?? '',
    }))
);

function render() {
  if (!chart || !chartEl.value) return;
  props.elements = props.elements.reverse();

  const formatMoney = (value) => getCurrencyFormatted(value, props.currency);

  const formatColor = function (params) {
    const el = props.elements[params.dataIndex];
    const percent = Math.round((el.fact / el.plan) * 100);
    if (props.type === 'EXPENSE') {
      if (percent >= 100) {
        return '#dc3545';
      }
      if (percent <= 75) {
        return '#198754';
      }
      return '#fc9800';
    } else {
      if (percent >= 100) {
        return '#198754';
      }
      if (percent <= 75) {
        return '#119b5b';
      }
      return '#10d077';
    }
  }

  chart.setOption({
    title: {
      text: props.title
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params) => {
        let res = params[0].name + '<br/>';

        params.forEach(p => {
          res += `${p.marker} ${p.seriesName}: ${formatMoney(p.value)}<br/>`;
        });

        return res;
      }
    },
    legend: {
      show: false
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      boundaryGap: [0, 0.01],
      axisLabel: {
        formatter: (value) => formatMoney(value)
      }
    },
    yAxis: {
      type: 'category',
      data: props.elements.map(el => el.name)
    },
    series: [
      {
        name: props.planTitle,
        type: 'bar',
        barWidth: 28,
        data: props.elements.map(el => el.plan),
        itemStyle: {
          color: 'rgba(5,35,128,0.5)',
          borderRadius: [0, 6, 6, 0],
        }
      },
      {
        name: props.factTitle,
        type: 'bar',
        barWidth: 28,
        data: props.elements.map(el => el.fact),
        itemStyle: {
          color: (params) => formatColor(params),
          borderRadius: [0, 6, 6, 0],
        },
      }
    ]
  }, true);
}

onMounted(() => {
  chart = echarts.init(chartEl.value);
  render();
});
watch(chartValues, () => {
  render();
});

onBeforeUnmount(() => {
  if (chart) {
    chart.dispose();
    chart = null;
  }
});
</script>
